/**
 * Map Profit Report period chips → inclusive ISO date range (Europe/London).
 * End never includes "today" — profit for the open day is not final.
 */

import { shiftYmd } from '../lib/calc';
import type { ProfitReportPeriod } from '../data/mockProfitPage';
import {
  londonDateString,
  londonLastMonthRange,
  startOfMonthYmd,
} from './ordersDailyPresentation';

/** Custom chip value for a calendar month. */
export function customMonthPeriod(month: string): ProfitReportPeriod {
  return `month:${month}`;
}

/** The YYYY-MM of a Custom month period, else null. */
export function customMonthOf(period: ProfitReportPeriod): string | null {
  const m = /^month:(\d{4}-\d{2})$/.exec(period);
  return m ? m[1] : null;
}

/** "September 2026" for "2026-09". */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Daily net profit chart title — one place for every period. */
export function profitChartTitle(period: ProfitReportPeriod): string {
  const month = customMonthOf(period);
  if (month) return `Daily net profit: ${monthLabel(month)}`;
  if (period === 'month-to-date') return 'Daily net profit: month to date';
  if (period === 'last-month') return 'Daily net profit: last month';
  return 'Daily net profit: last 7 days';
}

export function profitReportPeriodToRange(
  period: ProfitReportPeriod,
  asOf: Date = new Date(),
): { startDate: string; endDate: string } {
  const today = londonDateString(asOf);
  const yesterday = shiftYmd(today, -1);

  const month = customMonthOf(period);
  if (month) {
    // Whole calendar month; the current month stops at yesterday (today is not final).
    const start = `${month}-01`;
    const [y, m] = month.split('-').map(Number);
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const end = `${month}-${String(lastDay).padStart(2, '0')}`;
    return { startDate: start, endDate: end < yesterday ? end : yesterday };
  }

  if (period === 'yesterday') {
    return { startDate: yesterday, endDate: yesterday };
  }

  if (period === 'last-week') {
    // Seven calendar days ending yesterday (chip label: Last week).
    return { startDate: shiftYmd(yesterday, -6), endDate: yesterday };
  }

  if (period === 'last-month') {
    const { start, end } = londonLastMonthRange(asOf);
    return { startDate: start, endDate: end };
  }

  // month-to-date — 1st of month through yesterday (skip open today)
  const monthStart = startOfMonthYmd(yesterday);
  return { startDate: monthStart, endDate: yesterday };
}

/**
 * Inclusive range for the Profit daily bar chart.
 * Yesterday / Last week always plot the last 7 calendar days (KPIs/detail stay on the chip range).
 */
export function profitReportChartRange(
  period: ProfitReportPeriod,
  asOf: Date = new Date(),
): { startDate: string; endDate: string } {
  if (period === 'yesterday' || period === 'last-week') {
    return profitReportPeriodToRange('last-week', asOf);
  }
  return profitReportPeriodToRange(period, asOf);
}
